import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import toast from "react-hot-toast";

import MarkdownContent from "../../../../components/markdown/MarkdownContent";
import { ATTACHMENT_LABEL_PREFIX } from "../../../../components/markdown/markdownAttachment";
import type { TaskCell as TaskCellData } from "../../../../app/services/taskBoard";
import { useMessengerSocket } from "../../../../app/messenger/MessengerSocketContext";
import { useS3Upload } from "../../../../hooks/useS3Upload";
import { useTypingSender } from "../../../../hooks/useTypingSender";
import { isMacLike } from "../../../../utils/keyboardShortcuts";
import {
  convertBracketPairOnSpace,
  filterCellLinesByMentions,
  toggleCheckboxCheckedInLines,
  toggleCheckboxInText,
  toggleCheckboxPrefixInLines,
  type TextEditResult,
} from "./taskBoardUtils";
import ImageHoverZone from "./ImageHoverZone";

/**
 * セルの中身を包む器。
 *
 * 表の `td` は `vertical-align: middle`（グローバルの Table の既定）なので、
 * 行の高さが他セルに引き伸ばされると中身が行の中央へ浮く。表示中はそれで
 * 揃って見えるが、編集中のテキストエリアだけは書き出し位置が行の上端から
 * ずれて読みにくいため、編集中に限りセルいっぱいに広げて上端から始める。
 *
 * `$remoteEditing`（他ユーザーがこのセルを編集中）のときは枠を青くハイライト
 * し、同じセルへの同時書き込み（後勝ち上書き）を編集前に気付けるようにする。
 */
const CellWrapper = styled.div<{
  $editing: boolean;
  $remoteEditing: boolean;
  $dropping: boolean;
}>`
  position: relative;
  min-height: 28px;
  width: 100%;
  ${({ $editing }) =>
    $editing &&
    `
    height: 100%;
    display: flex;
    flex-direction: column;
  `}
  ${({ $remoteEditing }) =>
    $remoteEditing &&
    `
    outline: 1.5px solid var(--soremo-blue);
    outline-offset: 2px;
    border-radius: 4px;
  `}
  ${({ $dropping }) =>
    $dropping &&
    `
    outline: 1.5px dashed var(--soremo-blue);
    outline-offset: 2px;
    border-radius: 4px;
  `}
`;

/** 他ユーザーの編集中バッジ（セル右上に重ねる。クリックは透過） */
const RemoteEditBadge = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  max-width: calc(100% - 8px);
  padding: 1px 6px;
  border-radius: 8px;
  background: var(--soremo-blue);
  color: #fff;
  font-size: 10px;
  line-height: 1.5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
`;

/** 空セルのクリック領域（編集に入るための余白。表示テキストは持たない） */
const EmptyCellArea = styled.div`
  min-height: 22px;
  line-height: 22px;
  cursor: text;
`;

/** セル内に貼り付けた画像の表示上限。行の高さが暴れないよう contain で収める */
const CELL_IMAGE_MAX_HEIGHT_PX = 120;

const DisplayArea = styled.div`
  cursor: text;
  /* セル内 Markdown の余白を詰める */
  & p:first-child {
    margin-top: 0;
  }
  /* MarkdownContent の全画面向け max-height を上書きし、セル内に収める */
  & img {
    max-width: 100%;
    max-height: ${CELL_IMAGE_MAX_HEIGHT_PX}px;
    width: auto;
    height: auto;
    object-fit: contain;
  }
`;

// EditTextarea の寸法定数。スタイル定義と高さ計算（adjustHeight）で共用する
const FONT_SIZE_PX = 13;
const LINE_HEIGHT_FACTOR = 1.5;
const LINE_HEIGHT_PX = FONT_SIZE_PX * LINE_HEIGHT_FACTOR;
const TEXTAREA_PADDING_Y_PX = 4; // 上・下それぞれの padding
const MIN_TEXTAREA_HEIGHT = 60; // 約3行分。従来の固定高さと同じ最小値
const VIEWPORT_BOTTOM_MARGIN = 16; // 画面下端に張り付かせないための余白

/** 編集セルの上端からビューポート下端までを textarea の高さ上限とする */
const calcMaxHeight = (el: HTMLTextAreaElement) => {
  const { top } = el.getBoundingClientRect();
  return Math.max(
    window.innerHeight - top - VIEWPORT_BOTTOM_MARGIN,
    MIN_TEXTAREA_HEIGHT,
  );
};

const EditTextarea = styled.textarea`
  display: block;
  width: 100%;
  min-height: ${MIN_TEXTAREA_HEIGHT}px;
  padding: ${TEXTAREA_PADDING_Y_PX}px 6px;
  font-size: ${FONT_SIZE_PX}px;
  line-height: ${LINE_HEIGHT_FACTOR};
  border: none;
  outline: none;
  box-shadow: none;
  background: transparent;
  /* 高さは入力内容に応じて自動調整するため手動リサイズは無効化する */
  resize: none;
  overflow-y: hidden;
`;

/** ファイルアップロード中の待機表示（セル本文の上に小さく重ねる） */
const UploadingHint = styled.div`
  font-size: 11px;
  line-height: 1.4;
  color: var(--soremo-light-gray);
`;

/** placeholder に出すチェックボックスショートカットの表記（OS で出し分け） */
const CHECKBOX_SHORTCUT_LABEL = isMacLike ? "⌃C" : "Alt+C";

/**
 * セル内の添付（画像 `![alt](url)` と添付 `[📎 名前](url)`）。件数の判定に使う。
 * 通常のリンク（`[文字](url)`）は数えない。
 */
const MARKDOWN_ATTACHMENT_RE = new RegExp(
  String.raw`!\[[^\]]*\]\([^)]*\)|\[${ATTACHMENT_LABEL_PREFIX}[^\]]*\]\([^)]*\)`,
  "gu",
);

/** 1 セルに置ける添付の上限件数（ストレージと行高さの暴走を防ぐガード） */
const MAX_ATTACHMENTS_PER_CELL = 10;

/** 1 ファイルの上限サイズ。50MB 以上は multipart で上がる */
const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024 * 1024;
const MAX_ATTACHMENT_LABEL = "2GB";

/** セル本文に含まれる添付の件数 */
const countAttachments = (text: string): number =>
  text.match(MARKDOWN_ATTACHMENT_RE)?.length ?? 0;

/**
 * セル内に `<img>` で直接表示する画像か。HEIC / TIFF は多くのブラウザで
 * 描画できないため添付（チップ）として扱う。
 */
const isInlineImage = (file: File): boolean =>
  file.type.startsWith("image/") && !/hei[cf]|tiff/i.test(file.type);

/** 添付リンクのラベルとして安全な文字列にする（`[` `]` `\` をエスケープ） */
const escapeLinkLabel = (text: string): string =>
  text.replace(/[\\[\]]/g, (c) => `\\${c}`);

/** アップロード済みファイル 1 件ぶんの Markdown（画像は埋め込み、他は添付リンク） */
const toAttachmentMarkdown = (file: File, url: string): string =>
  isInlineImage(file)
    ? `![](${url})`
    : `[${ATTACHMENT_LABEL_PREFIX} ${escapeLinkLabel(file.name)}](${url})`;

/**
 * 添付の Markdown を本文の末尾へ行単位で追記する。
 *
 * セルの表示（MarkdownContent、breaks なし）では単一改行は段落内の
 * ソフトブレーク（空白）になるため、行を分けて追記した画像・チップは横に
 * 並んでセル幅で折り返される。行分割（splitTaskByLines）でも添付 1 件 = 1 行の
 * 単位が保たれる。
 */
const appendAttachmentMarkdown = (base: string, markdown: string): string => {
  const trimmed = base.replace(/\s+$/, "");
  return trimmed ? `${trimmed}\n${markdown}` : markdown;
};

/** ドラッグ中のデータにファイルが含まれるか（テキスト・行ドラッグ等は無視） */
const isFileDrag = (e: React.DragEvent): boolean =>
  Array.from(e.dataTransfer.types).includes("Files");

/** ドロップされたファイル（フォルダは除く） */
const droppedFiles = (dataTransfer: DataTransfer): File[] =>
  Array.from(dataTransfer.items)
    .filter(
      (item) =>
        item.kind === "file" && !item.webkitGetAsEntry?.()?.isDirectory,
    )
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null);

interface UploadedAttachment {
  file: File;
  url: string;
}

interface CellFileUploaderProps {
  files: File[];
  projectId: string;
  // eslint-disable-next-line no-unused-vars
  onFinish: (uploaded: UploadedAttachment[], failedCount: number) => void;
}

/**
 * アップロード中だけマウントする実行役。
 * useS3Upload（RTK Query の mutation 4 本 + 離脱ガード）を全セルに常駐させず、
 * アップロード中のセルだけが持つようにする。進捗表示もここで描く。
 */
const CellFileUploader: React.FC<CellFileUploaderProps> = ({
  files,
  projectId,
  onFinish,
}) => {
  const { uploadFiles, totalProgress } = useS3Upload({
    purpose: "task_board",
    projectId,
    skipRegister: true,
  });
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;
  // StrictMode の effect 二重実行で二重アップロードしないためのガード
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void uploadFiles(files).then((results) => {
      const uploaded = results.flatMap((r) =>
        r.status === "completed" && r.fileUrl
          ? [{ file: r.file, url: r.fileUrl }]
          : [],
      );
      onFinishRef.current(uploaded, files.length - uploaded.length);
    });
  }, [files, uploadFiles]);

  return (
    <UploadingHint>
      {files.length > 1 ? `${files.length} 件を` : ""}アップロード中...{" "}
      {Math.round(totalProgress)}%
    </UploadingHint>
  );
};

interface TaskCellProps {
  cell: TaskCellData | undefined;
  /** 編集可能か */
  editable: boolean;
  /** ファイルアップロードと編集中 presence のスコープになるプロジェクト ID */
  projectId: string;
  /** 編集中 presence のセル特定用（タスク行 ID）。未指定なら presence 無効 */
  taskId?: string;
  /** 編集中 presence のセル特定用（列 ID）。未指定なら presence 無効 */
  columnId?: string;
  /** このセルを編集中の他ユーザー表示名（編集中バッジ + 枠ハイライト） */
  remoteEditors?: readonly string[];
  // eslint-disable-next-line no-unused-vars
  onChangeContent: (content: string) => void;
  /**
   * 担当者フィルター中、表示を選択中の `@表示名` を含む行だけに絞る
   * （行単位分割）。null / 未指定 = 全文表示。編集時は常に全文を扱う。
   */
  visibleLineMentions?: string[] | null;
  /** `@表示名` の直後に差し込む要素（オファーステータスのバッジ等） */
  // eslint-disable-next-line no-unused-vars
  renderMentionSuffix?: (name: string) => React.ReactNode;
  /**
   * タスクリスト行の `@表示名` → チェックボックス色
   * （アクティビティ集計のユーザー配色）。undefined の名前は既定色のまま。
   */
  // eslint-disable-next-line no-unused-vars
  checkboxColorOf?: (name: string) => string | undefined;
}

const TaskCell: React.FC<TaskCellProps> = ({
  cell,
  editable,
  projectId,
  taskId,
  columnId,
  remoteEditors,
  onChangeContent,
  visibleLineMentions,
  renderMentionSuffix,
  checkboxColorOf,
}) => {
  const content = cell?.content ?? "";

  // 担当者フィルター中の行単位分割。表示（MarkdownContent）だけを担当行へ
  // 絞り、編集（textarea）・保存は常に全文を扱う（他の行が消えないように）。
  // null = 絞り込み不要（フィルターなし / 全行が該当）。
  const lineFilter = useMemo(
    () =>
      visibleLineMentions && visibleLineMentions.length > 0
        ? filterCellLinesByMentions(content, visibleLineMentions)
        : null,
    [content, visibleLineMentions],
  );
  const displayContent = lineFilter ? lineFilter.content : content;

  // 編集中 presence の送信（typing.task_board.start / stop）。
  // 他メンバーの画面では該当セルに「◯◯が編集中」バッジが表示される。
  const { sendEvent } = useMessengerSocket();
  const { onTypingChange, onTypingCommit } = useTypingSender({
    send: sendEvent,
    scope: "task_board",
    ids: {
      project_id: projectId,
      task_id: taskId,
      column_id: columnId,
    },
    enabled: editable && !!projectId && !!taskId && !!columnId,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(content);
  // アップロード中のファイル（null = アップロードしていない）
  const [uploadingFiles, setUploadingFiles] = useState<File[] | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 非同期のアップロード完了時に「最新の本文」「まだ編集中か」を同期的に
  // 参照するための ref（アップロード中に blur されても取りこぼさない）。
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const contentRef = useRef(content);
  contentRef.current = content;
  const isEditingRef = useRef(isEditing);
  isEditingRef.current = isEditing;

  /** 入力内容に合わせて textarea の高さをビューポート上限まで自動調整する */
  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    // 高さを一旦リセットし、内容全体の表示に必要な高さを scrollHeight で測る
    // （CSS の min-height が下限になる）
    el.style.height = "auto";
    const requiredHeight = el.scrollHeight;
    const maxHeight = calcMaxHeight(el);
    let nextHeight = requiredHeight;
    if (nextHeight > maxHeight) {
      // 上限で切り詰めるときは、最終行が中途半端に見切れないよう
      // 表示領域を行の高さの倍数に揃える
      const visibleLines = Math.floor(
        (maxHeight - TEXTAREA_PADDING_Y_PX * 2) / LINE_HEIGHT_PX,
      );
      nextHeight = Math.max(
        visibleLines * LINE_HEIGHT_PX + TEXTAREA_PADDING_Y_PX * 2,
        MIN_TEXTAREA_HEIGHT,
      );
    }
    el.style.height = `${nextHeight}px`;
    // 内容が収まりきらないときだけ textarea 内スクロールを許可する
    el.style.overflowY = requiredHeight > nextHeight ? "auto" : "hidden";
  }, []);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      const el = textareaRef.current;
      el.focus();
      // 末尾にカーソルを置く
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, [isEditing]);

  // 編集開始時と入力中（draft の変化）、高さを内容に追従させる
  useEffect(() => {
    if (isEditing) adjustHeight();
  }, [isEditing, draft, adjustHeight]);

  // 編集中は外部要因でも高さを再計算する。
  // - window resize: ビューポート上限（calcMaxHeight）が変わる
  // - ResizeObserver: 列リサイズ等での幅変化は折り返し行数を変える
  //   （adjustHeight 自身の高さ変更でも発火するため、幅が変わったときだけ再計算する）
  useEffect(() => {
    const el = textareaRef.current;
    if (!isEditing || !el) return;
    window.addEventListener("resize", adjustHeight);
    let lastWidth = el.clientWidth;
    const observer = new ResizeObserver(() => {
      if (el.clientWidth !== lastWidth) {
        lastWidth = el.clientWidth;
        adjustHeight();
      }
    });
    observer.observe(el);
    return () => {
      window.removeEventListener("resize", adjustHeight);
      observer.disconnect();
    };
  }, [isEditing, adjustHeight]);

  const startEdit = (e: React.MouseEvent) => {
    if (!editable) return;
    // チェックボックス・リンク・ボタンのクリックは編集に入らない
    const target = e.target as HTMLElement;
    if (target.closest("input, a, button")) return;
    // 画像のホバープレビューは表示エリア（ImageHoverZone）ごと外れて閉じる
    setDraft(content);
    setIsEditing(true);
  };

  const commit = () => {
    setIsEditing(false);
    onTypingCommit();
    if (draft !== content) {
      onChangeContent(draft);
    }
  };

  /**
   * ショートカットによるテキスト変換を反映する。setDraft（再描画）の後に
   * requestAnimationFrame で選択範囲を復元する（即時に setSelectionRange すると
   * controlled textarea の再描画で末尾へ飛ばされるため）。
   */
  const applyTextEdit = (result: TextEditResult) => {
    setDraft(result.text);
    onTypingChange();
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.setSelectionRange(result.selectionStart, result.selectionEnd);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      e.currentTarget.blur();
      return;
    }
    if (e.key === "Escape") {
      setDraft(content);
      setIsEditing(false);
      onTypingCommit();
      return;
    }

    // ── チェックボックス系ショートカット（IME 変換中は無視） ──
    if (e.nativeEvent.isComposing) return;
    const el = e.currentTarget;

    // 修飾キーは macOS = ⌃（Control）、それ以外 = Alt。
    // macOS の ⌥+英字は特殊文字の入力（⌥C=ç 等）になり、日本語 IME が有効だと
    // preventDefault でも挿入を抑止できないため使わない。⌃+英字は文字を
    // 生成せず、テキスト編集の既定バインド（⌃A/⌃E/⌃K 等）とも重ならない。
    // Windows / Linux の Alt+英字は文字を入力しないので Alt のままにする。
    // キーは物理位置（e.code）で判定する（配列・IME の影響を受けない）。
    const hasCellShortcutModifier = isMacLike
      ? e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey
      : e.altKey && !e.metaKey && !e.ctrlKey && !e.shiftKey;
    if (hasCellShortcutModifier) {
      if (e.code === "KeyC") {
        // 行頭の `- [ ] ` を付け外し（複数行選択時は各行に適用）
        e.preventDefault();
        applyTextEdit(
          toggleCheckboxPrefixInLines(
            el.value,
            el.selectionStart,
            el.selectionEnd,
          ),
        );
        return;
      }
      if (e.code === "KeyX") {
        // `[ ]` と `[x]` の切り替え（複数行選択時は各行に適用）
        e.preventDefault();
        applyTextEdit(
          toggleCheckboxCheckedInLines(
            el.value,
            el.selectionStart,
            el.selectionEnd,
          ),
        );
        return;
      }
      return;
    }

    // 「[]」+スペース → `- [ ] `（行頭のみ。Notion 風の自動変換）
    if (e.key === " " && el.selectionStart === el.selectionEnd) {
      const converted = convertBracketPairOnSpace(el.value, el.selectionStart);
      if (converted) {
        e.preventDefault();
        applyTextEdit({
          text: converted.text,
          selectionStart: converted.caret,
          selectionEnd: converted.caret,
        });
      }
    }
  };

  // MarkdownContent の components（useMemo）の依存に入るため、参照を安定させて
  // 本文の不要な再構築（＝添付チップ等の再マウント）を避ける。
  // 行単位分割の表示中は、表示上のチェックボックス番号を対応表で元本文の
  // 番号へ変換してからトグルする（保存は常に全文）。
  const handleCheckboxToggle = useCallback(
    (index: number, checked: boolean) => {
      if (!editable) return;
      const originalIndex = lineFilter
        ? lineFilter.checkboxIndexMap[index]
        : index;
      if (originalIndex === undefined) return;
      onChangeContent(toggleCheckboxInText(content, originalIndex, checked));
    },
    [editable, content, lineFilter, onChangeContent],
  );

  /**
   * 貼り付け・ドロップされたファイルを S3 へ上げ、本文の末尾へ追記する。
   * 既存の添付は置き換えない（上限は 1 セル MAX_ATTACHMENTS_PER_CELL 件）。
   */
  const startUpload = (files: File[]) => {
    if (!editable || uploadingFiles || files.length === 0) return;

    const sizeOk = files.filter((f) => f.size < MAX_ATTACHMENT_BYTES);
    if (sizeOk.length < files.length) {
      toast.error(
        `セルに添付できるファイルは ${MAX_ATTACHMENT_LABEL} 未満のみです`,
      );
    }

    // 既存の件数と合わせて上限を超える分は添付しない
    const base = isEditingRef.current ? draftRef.current : contentRef.current;
    const capacity = MAX_ATTACHMENTS_PER_CELL - countAttachments(base);
    if (capacity <= 0) {
      toast.error(
        `1 セルに置ける添付は ${MAX_ATTACHMENTS_PER_CELL} 件までです`,
      );
      return;
    }
    const accepted = sizeOk.slice(0, capacity);
    if (accepted.length < sizeOk.length) {
      toast.error(
        `1 セルに置ける添付は ${MAX_ATTACHMENTS_PER_CELL} 件までです` +
          `（超過分 ${sizeOk.length - accepted.length} 件は添付しません）`,
      );
    }
    if (accepted.length > 0) setUploadingFiles(accepted);
  };

  /**
   * アップロード完了時の差し込み。アップロード中に blur して編集が終わる
   * ことがあるため、差し込み先は state ではなく ref（最新の本文）から作る。
   * 編集中なら下書きへ、編集していなければそのまま保存へ回す。
   */
  const handleUploadFinish = (
    uploaded: UploadedAttachment[],
    failedCount: number,
  ) => {
    setUploadingFiles(null);
    if (failedCount > 0) {
      toast.error(`${failedCount} 件のアップロードに失敗しました`);
    }
    if (uploaded.length === 0) return;
    const markdown = uploaded
      .map(({ file, url }) => toAttachmentMarkdown(file, url))
      .join("\n");
    if (isEditingRef.current) {
      const next = appendAttachmentMarkdown(draftRef.current, markdown);
      draftRef.current = next;
      setDraft(next);
    } else {
      onChangeContent(appendAttachmentMarkdown(contentRef.current, markdown));
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!editable) return;
    const files = Array.from(e.clipboardData.files);
    if (files.length === 0) return;
    e.preventDefault();
    startUpload(files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!editable || !isFileDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = uploadingFiles ? "none" : "copy";
    if (!isDragOver) setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // 子要素間の移動でも dragleave が飛ぶため、セルの外へ出たときだけ解除する
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    if (!editable || !isFileDrag(e)) return;
    e.preventDefault();
    setIsDragOver(false);
    startUpload(droppedFiles(e.dataTransfer));
  };

  const hasRemoteEditors = !!remoteEditors && remoteEditors.length > 0;

  return (
    <CellWrapper
      $editing={isEditing}
      $remoteEditing={hasRemoteEditors}
      $dropping={isDragOver}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {hasRemoteEditors && (
        <RemoteEditBadge
          aria-label={`${remoteEditors.join("、")}さんが編集中`}
        >
          <span
            className="material-symbols-rounded"
            style={{ fontSize: 10, color: "inherit" }}
            aria-hidden="true"
          >
            edit
          </span>
          {remoteEditors.join("、")}が編集中
        </RemoteEditBadge>
      )}
      {uploadingFiles && (
        <CellFileUploader
          files={uploadingFiles}
          projectId={projectId}
          onFinish={handleUploadFinish}
        />
      )}
      {isEditing ? (
        <EditTextarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            onTypingChange();
          }}
          onBlur={commit}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={`マークダウンで入力（${CHECKBOX_SHORTCUT_LABEL} か - [ ] でチェックボックス、6/20 で期限、@表示名 で担当、ファイルは貼り付けかドロップ）`}
        />
      ) : displayContent.trim() ? (
        <DisplayArea onClick={startEdit}>
          <ImageHoverZone>
            <MarkdownContent
              content={displayContent}
              attachmentDisplay="preview"
              interactiveCheckboxes={editable}
              onCheckboxToggle={handleCheckboxToggle}
              renderMentionSuffix={renderMentionSuffix}
              checkboxColorOf={checkboxColorOf}
            />
          </ImageHoverZone>
        </DisplayArea>
      ) : (
        <EmptyCellArea onClick={startEdit} />
      )}
    </CellWrapper>
  );
};

export default TaskCell;
